import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useWebSocket } from '../WebSocketContext/WebSocketContext';
import { abaDeveriaPiscar } from '../../utils/piscarDaAbaDeChat';
import { Box, Tooltip, useTheme, IconButton, alpha, styled, Menu, MenuItem, ListItemText } from '@mui/material';
import { Close as CloseIcon, DragIndicator, PushPin, PushPinOutlined } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Styled Components – Abas no estilo navegador/Movidesk
 * Barra de abas com abas independentes, alternância e fechamento individual.
 */

const getQuickAccessPalette = (theme) => theme.palette.quickAccess ?? {};

/**
 * O realce do alfinete sob o mouse, em alfa de branco.
 *
 * Precisa ser mais claro que TODOS os fundos que a aba usa
 * (`palette.quickAccess.tab*Bg`, nos dois temas) -- e a primeira tentativa não
 * era: 22% contra uma aba de 20% ou 22% dá 0,02 de diferença, que é invisível.
 * Relatado na tela em 03/11/2026 ("não está tendo efeito de hover no pin").
 *
 * O teste não compara com número escrito à mão: ele LÊ os dois temas e exige
 * folga. Os alfas da aba já existem em três lugares (os dois temas e os
 * alternativos do `AppThemeGlobalStyles`); uma quarta cópia aqui passaria
 * verde enquanto a tela voltava ao defeito.
 */
export const ALFA_DO_REALCE_DO_ALFINETE = 0.42;

// Barra de abas (container) — sobre o header do shell (--sidebar-shell-bg)
const TabBar = styled(Box)(({ theme }) => {
  const qa = getQuickAccessPalette(theme);
  return {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 0,
    minHeight: 40,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': { height: 6 },
    '&::-webkit-scrollbar-thumb': {
      background: qa.tabInactiveBorder ?? 'rgba(255,255,255,0.25)',
      borderRadius: 3,
    },
  };
});

const QuickAccessTab = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'blink',
})(({ theme, active, blink }) => {
  const qa = getQuickAccessPalette(theme);
  const accent = qa.tabActiveAccent ?? theme.palette.primary.main;
  // Aba de chat recém-atribuído (item 8): pisca até o operador dar foco. Usa o acento
  // do tema (funciona no claro e no escuro).
  const blinkStyles = blink
    ? {
        borderColor: accent,
        borderBottom: `2px solid ${accent}`,
        animation: 'qaTabBlink 1.05s ease-in-out infinite',
        '@keyframes qaTabBlink': {
          '0%, 100%': { backgroundColor: 'transparent' },
          '50%': { backgroundColor: alpha(accent, 0.35) },
        },
      }
    : {};
  const baseStyles = {
    flex: '0 1 auto',
    minWidth: 120,
    maxWidth: 220,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Âncora do alfinete não fixado, que flutua para não cobrar largura do
    // rótulo. Ver `TabPinButton`.
    position: 'relative',
    gap: 6,
    paddingLeft: 12,
    paddingRight: 6,
    borderRadius: '10px 10px 0 0',
    cursor: 'pointer',
    userSelect: 'none',
    overflow: 'hidden',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
    border: '1px solid transparent',
    flexShrink: 1,
    marginRight: 2,
    marginBottom: -1,
    '& .MuiIconButton-root': { color: 'inherit' },
    '& .MuiSvgIcon-root': { color: 'inherit' },
    '& button': { color: 'inherit' },
    '&:active': {
      transform: 'scale(0.99)',
    },
  };

  if (!active) {
    return {
      ...baseStyles,
      backgroundColor: qa.tabInactiveBg ?? 'rgba(255, 255, 255, 0.12)',
      color: qa.tabInactiveText ?? 'rgba(255, 255, 255, 0.72)',
      borderColor: qa.tabInactiveBorder ?? 'rgba(255, 255, 255, 0.2)',
      borderBottom: '2px solid transparent',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: qa.tabInactiveHoverBg ?? 'rgba(255, 255, 255, 0.2)',
        color: qa.tabInactiveText ?? 'rgba(255, 255, 255, 0.85)',
        borderColor: qa.tabInactiveBorder ?? 'rgba(255, 255, 255, 0.28)',
      },
      ...blinkStyles,
    };
  }

  const activeText = qa.tabActiveText ?? 'rgba(255, 255, 255, 0.98)';
  return {
    ...baseStyles,
    backgroundColor: qa.tabActiveBg ?? 'rgba(255, 255, 255, 0.22)',
    color: activeText,
    borderColor: qa.tabActiveBorder ?? 'rgba(255, 255, 255, 0.32)',
    borderBottom: `2px solid ${accent}`,
    boxShadow: 'none',
    zIndex: 1,
    '&:hover': {
      backgroundColor: qa.tabActiveBg ?? 'rgba(255, 255, 255, 0.26)',
      color: qa.tabActiveText ?? 'rgba(255, 255, 255, 0.98)',
      borderColor: qa.tabActiveBorder ?? 'rgba(255, 255, 255, 0.36)',
    },
  };
});

// Label da aba
const TabLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  flex: '1 1 auto',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontSize: 13,
  lineHeight: 1.25,
  fontWeight: active ? 600 : 500,
}));

// Handle de arrastar (ícone de reordenar)
const TabDragHandle = styled('button')(() => ({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  padding: 0,
  margin: 0,
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  opacity: 0.65,
  cursor: 'grab',
  transition: 'opacity 0.15s ease',
  borderRadius: 4,
  '&:hover': { opacity: 1 },
  '&:active': { cursor: 'grabbing' },
}));

/**
 * O ALFINETE da aba (Gisele: "fixar as guias mais utilizadas").
 *
 * Desenho do dono do produto em 03/11/2026: "pode ter um pin nela ou algo ao
 * passar o mouse e daí clica e fixa para aquele usuário".
 *
 * ## Por que ele aparece ao passar o mouse, mas FICA quando está fixado
 *
 * Numa aba FIXADA o alfinete não é um botão: é o ESTADO. Sumir ao tirar o
 * mouse tiraria a única marca que diz por que aquela aba está sempre na
 * frente -- e quem não soubesse não teria onde desfazer.
 *
 * ## O alfinete NÃO FIXADO flutua, e isso é medição, não estética
 *
 * Ele entrava no fluxo com `opacity: 0`, para a largura da aba não pular
 * quando o mouse entrasse. Medido em produção em 03/11, logo depois do
 * deploy, o preço disso apareceu:
 *
 * | rótulo | com o alfinete no fluxo | sem ele |
 * |---|---|---|
 * | Tickets | 22px | 48px |
 * | Painel | 22px | 48px |
 * | Histórico | 31px | 49px |
 *
 * Cada aba perdia 26px de rótulo -- mais da metade --, e a barra passou a
 * mostrar "Ti...", "P...", "Configu...". Vinte pixels por aba, vezes onze
 * abas, numa barra que já disputava largura.
 *
 * Fora do fluxo (`absolute`), o rótulo volta exatamente ao que era. O custo é
 * o alfinete cobrir o fim do rótulo enquanto o mouse está em cima -- e aí a
 * pessoa está mirando um botão, não lendo o nome. O fundo sólido existe para
 * ele não ficar por cima das letras.
 *
 * A aba FIXADA mantém o alfinete no fluxo: ali ele é permanente, e são
 * poucas.
 */
const TabPinButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'fixada',
})(({ theme, fixada }) => ({
  flex: '0 0 auto',
  width: 20,
  height: 20,
  padding: 0,
  borderRadius: 4,
  color: 'inherit',
  opacity: fixada ? 1 : 0,
  transition: 'opacity 0.15s ease, background-color 0.15s ease',

  /*
   * A COR DE DESTAQUE SIGNIFICA "FIXADA", e nada mais aqui a usa.
   *
   * ## Nenhuma COR serve aqui, e isso foi medido
   *
   * A tentativa natural -- pintar o fixado com a cor de destaque -- falha
   * nesta barra, e as duas falham diferente (medições de 03/11/2026, tema
   * CLARO, contra o fundo efetivo da aba):
   *
   * | cor do ícone fixado | contraste |
   * |---|---|
   * | `primary.main` (azul escuro no tema claro) | **1,82:1** |
   * | `quickAccess.tabActiveAccent` | **2,06:1** |
   * | não fixado, branco a 72% | 3,35:1 |
   *
   * O mínimo para elemento gráfico é 3:1 -- as duas ficavam MENOS visíveis
   * que o alfinete não fixado. A inversão relatada, por outro caminho.
   *
   * E não é questão de escolher melhor: a barra é escura nos dois temas
   * (fundo efetivo ~`rgb(89,116,138)`), e para um ícone chegar a 3:1 ali
   * é preciso luminância ≈0,74 -- quase branco. **Nenhuma cor saturada
   * passa.**
   *
   * Então o estado é dito por duas coisas que funcionam:
   *
   * - a FORMA: alfinete cheio (`PushPin`) × contorno (`PushPinOutlined`);
   * - a FORÇA: branco cheio (`tabActiveText`) × branco a 72%.
   *
   * ## `&&` não é enfeite
   *
   * `QuickAccessTab` pinta `& .MuiSvgIcon-root` com `inherit`, e as duas
   * regras têm a mesma especificidade -- quem vence passaria a depender da
   * ordem em que o emotion injeta o CSS.
   */
  '&& .MuiSvgIcon-root': {
    fontSize: 13,
    color: fixada
      ? (getQuickAccessPalette(theme).tabActiveText ?? theme.palette.common.white)
      : 'inherit',
  },

  ...(fixada ? {} : {
    // Fora do fluxo: não cobra largura do rótulo. `right` o encosta à
    // esquerda do X, que tem 22px.
    position: 'absolute',
    right: 26,
    /*
     * Herda o fundo DA ABA, e não `background.paper`.
     *
     * Com o fundo de papel, o alfinete NÃO fixado virava um quadradinho
     * sólido e destacado ao passar o mouse -- lia-se como "ligado". E o
     * fixado, que não tem fundo, lia-se como "desligado". As duas leituras
     * trocadas; relatado na tela em 03/11/2026.
     *
     * Herdando, ele some dentro da aba: serve só para o ícone não ficar
     * por cima das letras do rótulo, que é a única razão de haver fundo.
     */
    backgroundColor: 'inherit',
  }),

  '.quick-access-tab:hover &': { opacity: 1 },

  /*
   * O realce de hover é NEUTRO, e precisa ser MAIS CLARO que a aba.
   *
   * Neutro porque com a cor de destaque aqui, passar o mouse pareceria
   * fixar -- era metade da inversão relatada.
   *
   * Mais claro porque a primeira tentativa foi branco a 22%, e a aba já é
   * branco a 20% (inativa com o mouse em cima) ou 22% (ativa): o realce
   * ficava com 0,02 de diferença de alfa, ou seja, invisível. Relatado na
   * tela logo depois -- "não está tendo efeito de hover no pin".
   *
   * Os dois valores estão nomeados no topo do arquivo, e há teste afirmando
   * a RELAÇÃO entre eles -- não o número.
   */
  '&:hover': {
    opacity: 1,
    backgroundColor: alpha(theme.palette.common.white, ALFA_DO_REALCE_DO_ALFINETE),
  },
  // Teclado: sem isto o alfinete é inalcançável para quem não usa mouse.
  '&:focus-visible': { opacity: 1 },
}));

// Botão fechar (visível ao hover na aba)
const TabCloseButton = styled(IconButton)(({ theme }) => ({
  flex: '0 0 auto',
  width: 22,
  height: 22,
  padding: 0,
  borderRadius: 4,
  color: 'inherit',
  opacity: 0.8,
  transition: 'opacity 0.15s ease, background-color 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: 14, color: 'inherit' },
  '&:hover': {
    opacity: 1,
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    color: theme.palette.error.main,
    '& .MuiSvgIcon-root': { color: theme.palette.error.main },
  },
}));

/**
 * Componente de item sortable individual
 */
const SortableItem = ({ item, index, getItemLabel, getItemTooltip, maxLabelLength, onItemClick, onItemRemove, getItemPath, handleItemClick, handleRemoveItem, theme, isActive, piscando = false, estaFixada, onTogglePin }) => {
  // Gera ID único para o item
  const itemId = item.id !== undefined && item.id !== null ? item.id : (item.key !== undefined && item.key !== null ? item.key : `item-${index}`);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    minWidth: 0,
    flexShrink: 1,
  };

  const fixada = typeof estaFixada === 'function' ? Boolean(estaFixada(item)) : false;
  const displayText = getItemLabel ? getItemLabel(item) : String(item);
  // O texto será truncado via CSS ellipsis, então sempre passamos o texto completo
  // O tooltip mostrará o texto completo quando necessário
  const tooltipText = getItemTooltip
    ? getItemTooltip(item)
    : displayText;

  return (
    <div ref={setNodeRef} style={style}>
      <Tooltip
        title={tooltipText}
        arrow
        placement="bottom"
        enterDelay={300}
      >
        <QuickAccessTab
          className="quick-access-tab"
          active={isActive}
          blink={piscando}
          onClick={() => handleItemClick(item)}
        >
          <TabDragHandle
            aria-label="Reordenar"
            {...attributes}
            {...listeners}
          >
            <DragIndicator fontSize="small" />
          </TabDragHandle>
          <TabLabel active={isActive} title={displayText}>
            {displayText}
          </TabLabel>
          {onTogglePin && (
            <TabPinButton
              size="small"
              fixada={fixada ? 1 : 0}
              onClick={(e) => {
                // Sem isto, fixar NAVEGA: o clique sobe para a aba inteira,
                // que é o que abre a página.
                e.stopPropagation();
                onTogglePin(item);
              }}
              aria-label={fixada ? `Desafixar ${displayText}` : `Fixar ${displayText}`}
            >
              {fixada ? <PushPin /> : <PushPinOutlined />}
            </TabPinButton>
          )}
          {onItemRemove && (
            <TabCloseButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveItem(e, item);
              }}
              aria-label="Fechar aba"
            >
              <CloseIcon />
            </TabCloseButton>
          )}
        </QuickAccessTab>
      </Tooltip>
    </div>
  );
};

/**
 * Componente genérico de acesso rápido com suporte a drag-and-drop
 * @param {Array} items - Array de itens para exibir
 * @param {Function} onItemClick - Função chamada ao clicar no item
 * @param {Function} onItemRemove - Função chamada ao remover o item
 * @param {Function} onReorder - Função chamada quando os itens são reordenados
 * @param {Function} getItemLabel - Função para obter o label do item
 * @param {Function} getItemTooltip - Função para obter o tooltip do item (opcional)
 * @param {Function} getItemPath - Função para obter o path do item (opcional, usado para navegação automática)
 * @param {Number} maxWidth - Largura máxima do container
 * @param {Number} maxLabelLength - Comprimento máximo do label antes de truncar
 * @param {Boolean} enableDrag - Habilita drag-and-drop (padrão: true)
 * @param {Function} onLimitReached - Callback quando o limite de espaço é atingido
 */
const QuickAccess = forwardRef(({
  items = [],
  onItemClick,
  onItemRemove,
  onReorder,
  getItemLabel,
  getItemTooltip,
  getItemPath,
  maxWidth = { xs: '80vw', sm: '80vw', md: '80vw' },
  maxLabelLength = 30,
  enableDrag = true,
  onLimitReached,
  /*
   * AS GUIAS FIXADAS (Gisele; desenho do dono do produto, 03/11/2026).
   *
   * Opcionais: sem elas o alfinete não aparece, e este componente continua
   * sendo o que era. Quem decide o que está fixado é o `QuickAccessUnified`,
   * que é quem conhece o par (tipo, item) de cada aba -- aqui dentro só há
   * "o item", e cada tipo guarda o id num campo diferente.
   */
  estaFixada,
  onTogglePin,
}, ref) => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  /*
   * As conversas com mensagem nova, para decidir o piscar.
   *
   * Sinal VIVO e de sessão, mantido pelo `WebSocketContext`: entra no
   * `NEW_CHAT_MESSAGE_EVENT` (só mensagem do cliente) e sai no
   * `MESSAGE_READ_EVENT`. A regra em si mora em `utils/piscarDaAbaDeChat`.
   */
  const { newMessageChats } = useWebSocket();
  const [activeId, setActiveId] = React.useState(null);
  const [overflowAnchor, setOverflowAnchor] = React.useState(null);
  const containerRef = useRef(null);
  const [maxItems, setMaxItems] = React.useState(1);

  // Constantes para cálculo de limite (abas estilo navegador)
  const TAB_MIN_WIDTH = 120;
  const TAB_GAP = 6;
  const PADDING_X = 16;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Precisa mover 8px para iniciar o drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Calcula maxItems baseado na largura do container
  const calculateMaxItems = React.useCallback(() => {
    if (!containerRef.current) {
      setMaxItems(1);
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const totalTabSpace = TAB_MIN_WIDTH + TAB_GAP;
    const availableWidth = containerWidth - PADDING_X;
    const calculatedMaxItems = Math.max(1, Math.floor(availableWidth / totalTabSpace));
    
    setMaxItems(calculatedMaxItems);
  }, []);

  // Atualiza maxItems quando o container é montado ou redimensionado
  React.useEffect(() => {
    // Calcula inicialmente
    calculateMaxItems();

    // Observa mudanças no tamanho do container
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateMaxItems();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateMaxItems]);

  useImperativeHandle(ref, () => ({
    // Sempre permite adicionar: o excedente vai para o menu de overflow (+N)
    canAddItem: () => true,
    getMaxItems: () => {
      return maxItems;
    },
    getContainerWidth: () => {
      return containerRef.current ? containerRef.current.clientWidth : 0;
    },
    getScrollWidth: () => {
      return containerRef.current ? containerRef.current.scrollWidth : 0;
    }
  }));

  // Overflow substitui o bloqueio por limite; onLimitReached mantido por compat.
  void onLimitReached;

  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick(item);
    } else if (getItemPath) {
      const path = getItemPath(item);
      if (path) {
        history.push(path);
      }
    }
  };

  // Função para verificar se um item está ativo
  const isItemActive = React.useCallback((item) => {
    if (!getItemPath) return false;
    
    const itemPath = getItemPath(item);
    if (!itemPath) return false;
    
    const currentPath = location.pathname;
    const currentSearch = location.search;
    const currentFullPath = `${currentPath}${currentSearch || ''}`;
    if (itemPath === currentFullPath) {
      return true;
    }
    
    // Para rooms, verifica se o roomId na URL corresponde
    if ((item.__quickAccessType === 'rooms' || item.room_id) && item.room_id) {
      const urlParams = new URLSearchParams(currentSearch);
      const roomId = urlParams.get('roomId');
      return roomId === String(item.room_id);
    }
    
    // Comparação exata do path
    if (itemPath === currentPath) {
      return true;
    }
    
    // Para tickets, verifica se o pathname corresponde a /ticket/{id}
    if ((item.__quickAccessType === 'tickets' || item.id) && item.id && currentPath.startsWith('/ticket/')) {
      const ticketId = currentPath.replace('/ticket/', '');
      return ticketId === String(item.id);
    }
    
    // Para páginas, compara o path
    if ((item.__quickAccessType === 'pages' || item.path) && item.path) {
      return currentPath === item.path;
    }
    
    return false;
  }, [location, getItemPath]);

  const handleRemoveItem = (e, item) => {
    e.stopPropagation();
    
    if (onItemRemove) {
      onItemRemove(item);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id && onReorder) {
      // Encontra os índices baseado nos IDs
      const oldIndex = items.findIndex((item, idx) => {
        const itemId = item.id !== undefined && item.id !== null ? item.id : (item.key !== undefined && item.key !== null ? item.key : `item-${idx}`);
        return itemId === active.id;
      });
      
      const newIndex = items.findIndex((item, idx) => {
        const itemId = item.id !== undefined && item.id !== null ? item.id : (item.key !== undefined && item.key !== null ? item.key : `item-${idx}`);
        return itemId === over.id;
      });
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(items, oldIndex, newIndex);
        onReorder(reorderedItems);
      }
    }
  };

  if (!items || items.length === 0) {
    return null;
  }

  // Se drag-and-drop não estiver habilitado ou não houver callback de reorder, renderiza sem drag
  // Nota: Removemos a restrição items.length <= 1 para permitir drag mesmo com 1 item (útil para preparar quando houver mais)
  if (!enableDrag || !onReorder) {
    return (
      <TabBar
        ref={containerRef}
        className="quick-access-bar"
        sx={{
          paddingLeft: '8px',
          paddingRight: '8px',
          maxWidth,
          width: '100%',
        }}
      >
        {items.map((item, index) => {
          const displayText = getItemLabel ? getItemLabel(item) : String(item);
          const tooltipText = getItemTooltip
            ? getItemTooltip(item)
            : displayText;
          const active = isItemActive(item);

          return (
            <Tooltip
              key={item.id || item.key || index}
              title={tooltipText}
              arrow
              placement="bottom"
              enterDelay={300}
            >
              <QuickAccessTab
                className="quick-access-tab"
                active={active}
                blink={abaDeveriaPiscar({ aba: item, salasComMensagemNova: newMessageChats, ativa: active })}
                onClick={() => handleItemClick(item)}
              >
                <TabLabel active={active} title={displayText}>
                  {displayText}
                </TabLabel>
                {/*
                  O alfinete também AQUI.

                  Esta barra tem dois caminhos de desenho -- com arrastar e
                  sem --, e o segundo é usado por quem monta a barra sem
                  `onReorder`. Pôr o alfinete só no primeiro faria a fixação
                  existir ou não dependendo de uma prop que não tem nada a
                  ver com ela, e sem erro nenhum para denunciar.
                */}
                {onTogglePin && (
                  <TabPinButton
                    size="small"
                    fixada={typeof estaFixada === 'function' && estaFixada(item) ? 1 : 0}
                    onClick={(e) => {
                      // Sem isto, fixar NAVEGA: o clique sobe para a aba.
                      e.stopPropagation();
                      onTogglePin(item);
                    }}
                    aria-label={
                      typeof estaFixada === 'function' && estaFixada(item)
                        ? `Desafixar ${displayText}`
                        : `Fixar ${displayText}`
                    }
                  >
                    {typeof estaFixada === 'function' && estaFixada(item)
                      ? <PushPin />
                      : <PushPinOutlined />}
                  </TabPinButton>
                )}
                {onItemRemove && (
                  <TabCloseButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveItem(e, item);
                    }}
                    aria-label="Fechar aba"
                  >
                    <CloseIcon />
                  </TabCloseButton>
                )}
              </QuickAccessTab>
            </Tooltip>
          );
        })}
      </TabBar>
    );
  }

  // Renderiza com drag-and-drop habilitado
  // Gera ID único do item (prioriza id, depois key, depois index)
  const getItemKey = (item, index) =>
    item.id !== undefined && item.id !== null
      ? item.id
      : (item.key !== undefined && item.key !== null ? item.key : `item-${index}`);

  const activeItem = items.find((item, index) => getItemKey(item, index) === activeId);

  // Divide em abas visíveis e ocultas (overflow). Reserva 1 slot para o botão +N
  // e promove a aba ativa para visível quando ela cairia no overflow.
  const hasOverflow = items.length > maxItems;
  let visibleItems = items;
  let hiddenItems = [];
  if (hasOverflow) {
    const visibleCount = Math.max(1, maxItems - 1);
    visibleItems = items.slice(0, visibleCount);
    hiddenItems = items.slice(visibleCount);
    const activeHiddenIdx = hiddenItems.findIndex((it) => isItemActive(it));
    if (activeHiddenIdx !== -1) {
      const promoted = hiddenItems[activeHiddenIdx];
      const displaced = visibleItems[visibleItems.length - 1];
      visibleItems = [...visibleItems.slice(0, -1), promoted];
      hiddenItems = [
        displaced,
        ...hiddenItems.slice(0, activeHiddenIdx),
        ...hiddenItems.slice(activeHiddenIdx + 1),
      ];
    }
  }
  const visibleItemIds = visibleItems.map((item, index) => getItemKey(item, index));

  const closeOverflowMenu = () => setOverflowAnchor(null);

  return (
    <>
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={visibleItemIds} strategy={horizontalListSortingStrategy}>
        <TabBar
          ref={containerRef}
          className="quick-access-bar"
          sx={{
            paddingLeft: '8px',
            paddingRight: '8px',
            maxWidth,
            width: '100%',
            overflowX: 'hidden',
          }}
        >
          {visibleItems.map((item, index) => {
            const itemId = getItemKey(item, index);
            const active = isItemActive(item);
            return (
            <SortableItem
              key={itemId}
              item={item}
              index={itemId}
              getItemLabel={getItemLabel}
              getItemTooltip={getItemTooltip}
              maxLabelLength={maxLabelLength}
              onItemClick={onItemClick}
              onItemRemove={onItemRemove}
              getItemPath={getItemPath}
              handleItemClick={handleItemClick}
              handleRemoveItem={handleRemoveItem}
              theme={theme}
              isActive={active}
              piscando={abaDeveriaPiscar({ aba: item, salasComMensagemNova: newMessageChats, ativa: active })}
              estaFixada={estaFixada}
              onTogglePin={onTogglePin}
            />
            );
          })}
          {hasOverflow && (
            <Tooltip title={`Mais ${hiddenItems.length} aba(s)`} arrow placement="bottom">
              <QuickAccessTab
                active={Boolean(overflowAnchor)}
                onClick={(e) => setOverflowAnchor(e.currentTarget)}
                sx={{ minWidth: 'auto', maxWidth: 'none', flex: '0 0 auto', justifyContent: 'center', paddingLeft: '12px', paddingRight: '12px' }}
                aria-label="Mostrar mais abas"
              >
                <TabLabel sx={{ flex: '0 0 auto' }}>{`+${hiddenItems.length}`}</TabLabel>
              </QuickAccessTab>
            </Tooltip>
          )}
        </TabBar>
      </SortableContext>
      <DragOverlay>
        {activeItem ? (
          <QuickAccessTab
            active={false}
            sx={{
              opacity: 0.92,
              transform: 'rotate(2deg)',
              boxShadow: 2,
            }}
          >
            <TabDragHandle aria-label="Reordenar">
              <DragIndicator fontSize="small" />
            </TabDragHandle>
            <TabLabel title={getItemLabel ? getItemLabel(activeItem) : String(activeItem)}>
              {getItemLabel ? getItemLabel(activeItem) : String(activeItem)}
            </TabLabel>
          </QuickAccessTab>
        ) : null}
      </DragOverlay>
    </DndContext>
    <Menu
      anchorEl={overflowAnchor}
      open={Boolean(overflowAnchor)}
      onClose={closeOverflowMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 220, maxWidth: 320 } } }}
    >
      {hiddenItems.map((item, index) => {
        const itemId = getItemKey(item, index);
        const label = getItemLabel ? getItemLabel(item) : String(item);
        const active = isItemActive(item);
        return (
          <MenuItem
            key={itemId}
            selected={active}
            onClick={() => {
              handleItemClick(item);
              closeOverflowMenu();
            }}
            sx={{ pr: 1 }}
          >
            <ListItemText
              primary={label}
              primaryTypographyProps={{ noWrap: true, fontSize: 13, fontWeight: active ? 600 : 500 }}
            />
            {onItemRemove && (
              <TabCloseButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveItem(e, item);
                }}
                aria-label="Fechar aba"
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </TabCloseButton>
            )}
          </MenuItem>
        );
      })}
    </Menu>
    </>
  );
});

QuickAccess.displayName = 'QuickAccess';

export default QuickAccess;

